import { Component, Input, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { Permissions } from 'src/app/shared/dto/permissions';
import { ReportInputTypes, StateTypes } from 'src/app/shared/models/common';
import { PermissionService } from 'src/app/utils/permission.service';
import { TopFilterService } from '../services/topFIlter.service';
import { AuthService } from 'src/app/auth/services/auth.service';

@Component({
  selector: 'app-top-filters',
  templateUrl: './top-filters.component.html',
  styleUrls: ['./top-filters.component.css'],
})
export class TopFiltersComponent implements OnInit {
  public formInstance: FormGroup;
  public type: 'client' | 'server' = 'client';
  public reportInputTypes = ReportInputTypes;
  public permissions = Permissions;

  public selectedAreaId: number = 0;
  public selectedBranchId: number = 0;

  public startDateRange = {
    min: new Date('01/01/2016'),
    max: new Date(),
  };
  public endDateRange = {
    min: new Date(this.startDateRange.max),
    max: new Date(),
  };

  constructor(
    private permissionsService: PermissionService,
    private topFilterService: TopFilterService,
    private authService: AuthService
  ) {
    this.formInstance = this.topFilterService.topFilterForm;
  }

  public reset() {
    const canArea = this.hasPermission([this.permissions.CAN_VIEW_OTHER_AREAS]);
    const canBranch = this.hasPermission([this.permissions.CAN_VIEW_OTHER_BRANCHES]);
    const user = this.authService.getuser;
    const areaId = !canArea && user?.area?.id ? user.area.id : 0;
    const branchId = !canArea && !canBranch && user?.branch?.id ? user.branch.id : 0;
    this.formInstance.patchValue({
      [ReportInputTypes.AREA]: areaId,
      [ReportInputTypes.BRANCH]: branchId,
      [ReportInputTypes.LOAN_OFFICER]: this.defaultLoanOfficerId,
    });
    this.selectedAreaId = areaId;
    this.selectedBranchId = branchId;
    this.rememberUserScope();
  }

  /* The clients list starts from the dashboard's Area / Branch, so every
     change here is recorded, with names, in the shared TopFilterService. */
  private rememberUserScope() {
    const user = this.authService.getuser;
    this.topFilterService.setScope(
      user?.area?.id ? { id: user.area.id, name: user.area.name } : null,
      user?.branch?.id ? { id: user.branch.id, name: user.branch.name } : null,
    );
  }

  /**
   * The account officer the dashboard opens on, like area and branch.
   * Branch-level users — who can see neither other areas nor other branches,
   * i.e. account officers — start on their own portfolio, the same rule as the
   * Clients list. Anyone wider (managers, admins) starts on All: they are often
   * not account officers themselves, and would otherwise open on an empty
   * dashboard.
   */
  private get defaultLoanOfficerId(): number {
    const canArea = this.hasPermission([this.permissions.CAN_VIEW_OTHER_AREAS]);
    const canBranch = this.hasPermission([
      this.permissions.CAN_VIEW_OTHER_BRANCHES,
    ]);
    if (canArea || canBranch) return 0;
    return this.authService.getuser?.id || 0;
  }

  public dateListner() {
    this.startDateRange.max = new Date(this.formInstance.get(ReportInputTypes.END_DATE).value);
    this.endDateRange.min = new Date(this.formInstance.get(ReportInputTypes.START_DATE).value)
  }

  

  public hasPermission(perms: string[]) {
    return this.permissionsService.hasPermissionList(perms);
  }

  get getAreaCtrl() {
    return this.formInstance.get(ReportInputTypes.AREA) as FormControl;
  }

  get getBranchCtrl() {
    return this.formInstance.get(ReportInputTypes.BRANCH) as FormControl;
  }

  get getLoanOfficerCtrl() {
    return this.formInstance.get(ReportInputTypes.LOAN_OFFICER) as FormControl;
  }

  get startDateCtrl() {
    return this.formInstance.get(ReportInputTypes.START_DATE) as FormControl;
  }

  get endDateCtrl() {
    return this.formInstance.get(ReportInputTypes.END_DATE) as FormControl;
  }

  get currencyCtrl() {
    return this.formInstance.get(ReportInputTypes.CURRENCY) as FormControl;
  }
  public emitSelectedVal(
    key: ReportInputTypes,
    val: { id: any; name: string }
  ) {
    switch (key) {
      case ReportInputTypes.AREA:
        this.selectedAreaId = val?.id || 0;
        this.selectedBranchId = 0;
        if (val?.id !== 0) {
          this.formInstance.get(ReportInputTypes.BRANCH).setValue(0);
          this.formInstance.get(ReportInputTypes.LOAN_OFFICER).setValue(0);
        }
        // A specific area clears the branch, as the form above does; All
        // leaves it as it was.
        this.topFilterService.setScope(
          val,
          val?.id === 0 ? this.topFilterService.currentScope().branch : null,
        );
        break;
      case ReportInputTypes.BRANCH:
        this.selectedBranchId = val?.id || 0;
        if (val?.id !== 0) {
          this.formInstance.get(ReportInputTypes.LOAN_OFFICER).setValue(0);
        }
        this.topFilterService.setScope(
          this.topFilterService.currentScope().area,
          val,
        );
        break;
      case ReportInputTypes.GROUP:
        break;
      case ReportInputTypes.LOAN_OFFICER:
        break;
    }
  }

  ngOnInit(): void {
    const user = this.authService.getuser;

    // Silently pre-populate from the logged-in user's profile.
    // Using emitEvent: false so child chart components don't fire on init —
    // each child triggers its own first load via subject.next() in their ngOnInit.
    this.formInstance.patchValue({
      [ReportInputTypes.AREA]: user?.area?.id,
      [ReportInputTypes.BRANCH]: user?.branch?.id,
      [ReportInputTypes.LOAN_OFFICER]: this.defaultLoanOfficerId,
    }, { emitEvent: false });

    // Seed after patchValue so these always match whatever is now in the form.
    this.selectedAreaId = this.formInstance.get(ReportInputTypes.AREA)?.value || 0;
    this.selectedBranchId = this.formInstance.get(ReportInputTypes.BRANCH)?.value || 0;

    this.rememberUserScope();
  }
}
